import {
  AutomationActionType,
  AutomationTaskInput,
  CandidateStatus,
  RequirementConditionOperator,
  RequirementConditionType
} from 'generated-graphql/types';
import { AnalysisRequirementInput } from 'modules/analyse/types/AnalysisRequirementInput';
import { convertWeightToText } from './convertWeightToText';
import { splitRulesAndActions } from './splitRulesAndActions';

type SummaryAnalyseItem = {
  label: string;
  value: string[];
};

type SummaryAnalyse = {
  ruleset: { label: string; value: string };
  externalId?: { label: string; value: string };
  mandatoryRequirements: SummaryAnalyseItem;
  optionalRequirements: SummaryAnalyseItem;
  rules: SummaryAnalyseItem;
  actions: SummaryAnalyseItem;
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case CandidateStatus.ShortListed:
      return 'shortlisted';
    case CandidateStatus.Rejected:
      return 'rejected';
    case CandidateStatus.NotProgressing:
      return 'not progressing';
    case CandidateStatus.Review:
      return 'review';
    case CandidateStatus.InterviewStage_1:
      return '1st interview stage';
    case CandidateStatus.InterviewStage_2:
      return '2nd interview stage';
    case CandidateStatus.Offer:
      return 'offer';
    case CandidateStatus.Progressing:
      return 'progressing';
    default:
      return status.replace(/_/g, ' ').toLowerCase();
  }
};

export const mapToSummaryAnalyse = ({
  rulesetChosenTitle,
  externalId,
  requirements,
  automationTasks,
  campaignNames
}: {
  rulesetChosenTitle?: string;
  externalId?: string;
  campaignNames: Record<string, string>;
  requirements: AnalysisRequirementInput[];
  automationTasks: AutomationTaskInput[];
}): SummaryAnalyse => {
  const { rules, actions } = splitRulesAndActions(automationTasks);

  const summaryAnalyse: SummaryAnalyse = {
    ruleset: { label: 'Ruleset', value: 'None chosen' },
    mandatoryRequirements: {
      label: 'KO requirements',
      value: ['None added']
    },
    optionalRequirements: {
      label: 'Optional requirements',
      value: ['None added']
    },
    rules: { label: 'Rules', value: ['None added'] },
    actions: { label: 'Actions', value: ['None added'] }
  };

  if (rulesetChosenTitle) {
    summaryAnalyse.ruleset.value = rulesetChosenTitle;
  }

  if (externalId) {
    summaryAnalyse.externalId = { label: 'External ID', value: externalId };
  }

  const mandatoryRequirements: SummaryAnalyseItem = {
    label: 'KO requirements',
    value: []
  };

  const optionalRequirements: SummaryAnalyseItem = {
    label: 'Optional requirements',
    value: []
  };

  if (requirements.length) {
    requirements.forEach((requirement) => {
      if (requirement.isMandatory) {
        mandatoryRequirements.value.push(
          `(${convertWeightToText(requirement.weight)}) ${requirement.content}`
        );
      } else {
        optionalRequirements.value.push(
          `(${convertWeightToText(requirement.weight)}) ${requirement.content}`
        );
      }
    });

    if (mandatoryRequirements.value.length) {
      summaryAnalyse.mandatoryRequirements = mandatoryRequirements;
    }
    if (optionalRequirements.value.length) {
      summaryAnalyse.optionalRequirements = optionalRequirements;
    }
  }

  const rulesItem: SummaryAnalyseItem = {
    label: 'Rules',
    value: []
  };

  if (rules.length) {
    rules.forEach((rule) => {
      const { requirementConditions, action } = rule;

      // Format rule based on conditions
      let ruleText = '';

      // Check for score condition
      const scoreCondition = requirementConditions.find(
        (condition) =>
          condition.type === RequirementConditionType.CandidateScoreCondition ||
          condition.type === RequirementConditionType.ConversationScore
      );

      // Check for requirements met condition
      const requirementsMetCondition = requirementConditions.find(
        (condition) =>
          condition.type ===
            RequirementConditionType.CandidateRequirementsMetCondition ||
          RequirementConditionType.ConversationMandatoryQuestionsMet
      );

      if (scoreCondition) {
        const score =
          scoreCondition.scoreScaled ?? scoreCondition.preferredAnswerScore;

        if (score === null || score === undefined) {
          return;
        }

        const percentage = Math.round(score * 100);
        const operator =
          scoreCondition.operator ===
          RequirementConditionOperator.EqualToOrGreaterThan
            ? 'greater than or equal to'
            : 'less than or equal to';
        const status = action.candidateStatus || '';
        const statusLabel = getStatusLabel(status);

        // Format with line breaks
        if (requirementsMetCondition) {
          const isMet =
            requirementsMetCondition.operator ===
            RequirementConditionOperator.IsTrue;
          const isConversationScore =
            scoreCondition.type === RequirementConditionType.ConversationScore;
          const metText = isConversationScore ? 'satisfied' : 'met';

          ruleText = `If ${
            isConversationScore ? 'knockout questions' : 'KO requirements'
          } are ${isMet ? '' : 'not '}${metText},\nand ${
            isConversationScore ? 'the preferred answer' : 'analysis'
          } score is ${operator} ${percentage}%,\nthen change status to ${statusLabel}`;
        } else {
          ruleText = `When knockout requirements have been met, and they score ${operator} ${percentage}%,\nthen change status to ${statusLabel}`;
        }
      }

      if (ruleText) {
        rulesItem.value.push(ruleText);
      }
    });

    if (rulesItem.value.length) {
      summaryAnalyse.rules = rulesItem;
    }
  }

  const actionsItem: SummaryAnalyseItem = {
    label: 'Actions',
    value: []
  };

  if (actions.length) {
    actions.forEach((action) => {
      const actionType = action.action.type;
      let actionText = '';
      const campaignId = action.action.campaignId || '';

      const candidateStatus =
        action.requirementConditions[0].candidateStatus || '';
      const statusLabel = getStatusLabel(candidateStatus);

      switch (actionType) {
        case AutomationActionType.SendCampaign:
          actionText = `If candidate status is ${statusLabel},\nthen add to campaign: ${campaignNames[campaignId]}`;
          break;
        case AutomationActionType.SendEmail:
          actionText = `If candidate status is ${statusLabel},\nthen send email to candidate`;
          break;
        case AutomationActionType.UpdateAts:
          actionText = `If candidate status is ${statusLabel},\nthen update ATS status`;
          break;
        default:
          actionText = `If candidate status is ${statusLabel},\nthen ${actionType
            .replace(/_/g, ' ')
            .toLowerCase()}`;
      }

      if (actionText) {
        actionsItem.value.push(actionText);
      }
    });

    if (actionsItem.value.length) {
      summaryAnalyse.actions = actionsItem;
    }
  }

  return summaryAnalyse;
};
